/* global __ERPO_BUILD_VERSION__ */
const CHECK_INTERVAL_MS = 5 * 60 * 1000
export const FRONTEND_UPDATE_PENDING_EVENT = 'erpo:frontend-update-pending'
const reloadGuards = new Set()
let pendingReloadVersion = ''
let pendingTarget = ''
let checkTimer = null
let checking = null
let edited = false
let installed = false
let recoveryRequested = false
let manualRecovery = false

const recovery = () => window.__erpoFrontendRecovery
const canReloadNow = () => {
  for (const guard of reloadGuards) {
    try { if (guard({ version: pendingReloadVersion }) === false) return false } catch (_) { return false }
  }
  return true
}
const hasActiveForm = () => {
  const focused = document.activeElement
  const filledInput = focused?.matches('input, textarea, select, [contenteditable="true"]') &&
    String(focused.value || focused.textContent || '').trim()
  const visibleDialog = [...document.querySelectorAll('[role="dialog"], [aria-modal="true"]')]
    .some(element => element.getClientRects().length && element.getAttribute('aria-hidden') !== 'true')
  return edited || !!filledInput || visibleDialog
}

const notifyPendingReload = () => {
  window.dispatchEvent(new CustomEvent(FRONTEND_UPDATE_PENDING_EVENT, { detail: { version: pendingReloadVersion } }))
  recovery()?.show('页面有更新或部分内容未能加载，请保存当前内容后重新加载。', () => {
    if (!canReloadNow()) return
    if (hasActiveForm() && !window.confirm('请确认当前填写的内容已保存，再重新加载页面。')) return
    edited = false
    requestFrontendReload(pendingReloadVersion, { manual: true, confirmed: true })
  })
}

export const hasPendingFrontendReload = () => !!pendingReloadVersion

export const requestFrontendReload = (version = pendingReloadVersion || __ERPO_BUILD_VERSION__, options = {}) => {
  pendingReloadVersion = version
  if (!canReloadNow() || (!options.confirmed && hasActiveForm())) {
    notifyPendingReload()
    return false
  }
  if (!recovery()?.reload(version, pendingTarget, !!options.manual)) {
    notifyPendingReload()
    return false
  }
  pendingReloadVersion = ''
  return true
}

export const registerFrontendReloadGuard = (guard) => {
  if (typeof guard !== 'function') return () => {}
  reloadGuards.add(guard)
  return () => reloadGuards.delete(guard)
}

export const isChunkLoadError = (error) => /ChunkLoadError|Loading (?:CSS )?chunk [\s\S]*failed|Failed to fetch dynamically imported module|Importing a module script failed/i
  .test(String(error?.message || error || ''))

export const checkFrontendVersion = (forceRecovery = false, manual = false) => {
  recoveryRequested = recoveryRequested || forceRecovery
  manualRecovery = manualRecovery || manual
  if (checking) return checking
  checking = (async () => {
    try {
      const version = await recovery()?.fetchVersion()
      if (!version) {
        if (recoveryRequested) {
          recovery()?.show('页面未能加载，请检查网络后重试。', () => checkFrontendVersion(true, true))
        }
        return
      }
      // Compare against this tab's actual compiled code, never shared localStorage.
      const shouldReload = recoveryRequested || !!pendingReloadVersion || version !== __ERPO_BUILD_VERSION__
      const userRetry = manualRecovery
      recoveryRequested = false
      manualRecovery = false
      if (shouldReload) requestFrontendReload(version, { manual: userRetry })
    } catch (_) {
      // Background checks must never prevent normal app startup.
    } finally { checking = null }
  })()
  return checking
}

export const installFrontendRecovery = (router) => {
  if (installed) return
  installed = true
  const recover = (detail = {}) => {
    if (detail.target) pendingTarget = detail.target
    if (detail.manual) {
      if (pendingReloadVersion) requestFrontendReload(pendingReloadVersion, { manual: true })
      else checkFrontendVersion(true, true)
    } else checkFrontendVersion(true)
  }
  recovery()?.setHandler(recover)
  router.onError((error, to) => {
    if (isChunkLoadError(error)) recover({ target: to?.fullPath })
  })
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) recover()
  })
  // Conservative fallback for forms without a page-specific reload guard.
  const markEdited = (event) => {
    if (event.target?.matches('input, textarea, select, [contenteditable="true"]')) edited = true
  }
  document.addEventListener('input', markEdited, true)
  document.addEventListener('change', markEdited, true)
  router.afterEach((_to, _from, failure) => {
    if (!failure) { edited = false; pendingTarget = '' }
  })
}

export const startFrontendVersionCheck = () => {
  if (checkTimer) return
  checkFrontendVersion()
  checkTimer = window.setInterval(checkFrontendVersion, CHECK_INTERVAL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkFrontendVersion()
  })
  window.addEventListener('pageshow', () => checkFrontendVersion())
  window.addEventListener('online', () => checkFrontendVersion(recoveryRequested || !!pendingReloadVersion))
}
